<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { classesApi } from '@/api'
import type { ClassDetails } from '@/api'
import { ASSIGNABLE_COLOURS, COLOUR_MAP, type PaletteColour } from '@/constants/colors'
import { AppButton } from '@/design-system/components'
import { getErrorMessage } from '@/utils/error'

/**
 * ClassSettingsModal — web counterpart of Android's ClassSettingsScreen,
 * scoped to what the web API supports today (name, colour, delete).
 * Delete mirrors Android's type-the-name confirmation.
 */
const props = defineProps<{ modelValue: boolean; cls: ClassDetails }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  updated: [cls: ClassDetails]
  deleted: []
}>()

const MAX_NAME = 28

const name = ref('')
const colour = ref<PaletteColour>('RED')
const saving = ref(false)
const error = ref<string | null>(null)

// Delete confirmation sub-view
const confirmingDelete = ref(false)
const confirmName = ref('')
const deleting = ref(false)
const nameMatches = computed(() => confirmName.value.trim() === (props.cls.name ?? ''))

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      name.value = props.cls.name ?? ''
      colour.value = (props.cls.colour as PaletteColour) ?? 'RED'
      error.value = null
      confirmingDelete.value = false
      confirmName.value = ''
    }
  },
)

const dirty = computed(
  () => name.value.trim() !== (props.cls.name ?? '') || colour.value !== props.cls.colour,
)
const canSave = computed(() => !!name.value.trim() && dirty.value && !saving.value)

function close() {
  if (saving.value || deleting.value) return
  emit('update:modelValue', false)
}

async function save() {
  if (!canSave.value) return
  saving.value = true
  error.value = null
  try {
    const res = await classesApi.classesClassIdPut(props.cls.id!, {
      name: name.value.trim(),
      colour: colour.value,
    })
    emit('updated', res.data ?? { ...props.cls, name: name.value.trim(), colour: colour.value })
    emit('update:modelValue', false)
  } catch (err: unknown) {
    error.value = getErrorMessage(err, 'Failed to update class.')
  } finally {
    saving.value = false
  }
}

async function destroy() {
  if (!nameMatches.value || deleting.value) return
  deleting.value = true
  error.value = null
  try {
    await classesApi.classesClassIdDelete(props.cls.id!)
    emit('deleted')
    emit('update:modelValue', false)
  } catch (err: unknown) {
    error.value = getErrorMessage(err, 'Failed to delete class.')
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="csm-overlay">
      <div class="csm-backdrop" @click="close" />
      <div class="csm-card">
        <div class="csm-header">
          <h2>{{ confirmingDelete ? 'Delete this class?' : 'Class settings' }}</h2>
          <button class="csm-close" @click="close">
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Settings view -->
        <form v-if="!confirmingDelete" class="csm-body" @submit.prevent="save">
          <div>
            <label for="csmName" class="csm-label">Class name</label>
            <input
              id="csmName"
              v-model="name"
              type="text"
              :maxlength="MAX_NAME"
              placeholder="e.g. 8 Oasis"
              class="csm-input"
              :disabled="saving"
            />
          </div>

          <div>
            <label class="csm-label">Accent colour</label>
            <div class="csm-grid">
              <button
                v-for="c in ASSIGNABLE_COLOURS"
                :key="c"
                type="button"
                class="csm-swatch"
                :class="{ active: colour === c }"
                :style="{ backgroundColor: COLOUR_MAP[c].hex }"
                :title="COLOUR_MAP[c].name"
                @click="colour = c"
              >
                <FontAwesomeIcon v-if="colour === c" :icon="['fas', 'check']" />
              </button>
            </div>
          </div>

          <!-- Danger zone -->
          <div class="csm-danger">
            <div class="csm-danger-text">
              <p class="csm-danger-title">Delete this class</p>
              <p class="csm-danger-sub">
                Permanently removes the class, its register history, grades and notes. This can't
                be undone.
              </p>
            </div>
            <AppButton variant="danger" size="sm" @click="confirmingDelete = true">
              <FontAwesomeIcon :icon="['fas', 'trash-can']" class="csm-trash" /> Delete class
            </AppButton>
          </div>

          <div v-if="error" class="csm-error">{{ error }}</div>
          <div class="csm-actions">
            <AppButton variant="secondary" size="md" class="csm-flex" :disabled="saving" @click="close">
              Cancel
            </AppButton>
            <AppButton variant="primary" size="md" type="submit" class="csm-flex" :loading="saving" :disabled="!canSave">
              Save changes
            </AppButton>
          </div>
        </form>

        <!-- Delete confirmation view -->
        <div v-else class="csm-body">
          <p class="csm-confirm-lede">
            Deleting <strong>{{ cls.name }}</strong> permanently removes:
          </p>
          <ul class="csm-confirm-list">
            <li v-if="cls.studentCount">
              {{ cls.studentCount }} {{ cls.studentCount === 1 ? "student's" : "students'" }} records
            </li>
            <li>Register &amp; attendance history</li>
            <li>All grades and assignments</li>
            <li>Class notes</li>
          </ul>
          <div>
            <label for="csmConfirm" class="csm-label">Type the class name to confirm</label>
            <input
              id="csmConfirm"
              v-model="confirmName"
              type="text"
              :placeholder="cls.name"
              class="csm-input"
              :disabled="deleting"
            />
          </div>
          <div v-if="error" class="csm-error">{{ error }}</div>
          <div class="csm-actions">
            <AppButton variant="secondary" size="md" class="csm-flex" :disabled="deleting" @click="confirmingDelete = false">
              Cancel
            </AppButton>
            <AppButton variant="danger" size="md" class="csm-flex" :loading="deleting" :disabled="!nameMatches" @click="destroy">
              Delete class permanently
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.csm-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
}
.csm-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(12, 12, 14, 0.5);
  backdrop-filter: blur(4px);
}
.csm-card {
  position: relative;
  background: var(--surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  width: 100%;
  max-width: 28rem;
  overflow: hidden;
}
.csm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-lg) var(--space-xl);
  border-bottom: 1px solid var(--hairline);
}
.csm-header h2 {
  font-size: var(--text-title-md);
  font-weight: var(--weight-semibold);
  color: var(--ink-900);
  margin: 0;
}
.csm-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
  color: var(--ink-500);
  cursor: pointer;
}
.csm-close:hover {
  background: var(--surface-muted);
  color: var(--ink-900);
}
.csm-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  padding: var(--space-xl);
}
.csm-label {
  display: block;
  font-size: var(--text-body-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-700);
  margin-bottom: var(--space-6);
}
.csm-input {
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  background: var(--surface);
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  font-size: var(--text-body);
  color: var(--ink-900);
  font-family: inherit;
}
.csm-input:focus {
  outline: 2px solid var(--focus-ring);
  outline-offset: -1px;
  border-color: transparent;
}
.csm-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-sm);
}
.csm-swatch {
  aspect-ratio: 1;
  width: 100%;
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: transform var(--motion-fast);
}
.csm-swatch:hover {
  transform: scale(1.05);
}
.csm-swatch.active {
  outline: 2px solid var(--ink-black);
  outline-offset: 2px;
  transform: scale(1.05);
}
.csm-danger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-md);
  border: 1px solid var(--danger-outline, #f3c1c1);
  background: var(--danger-soft);
  border-radius: var(--radius-md);
}
.csm-danger-text {
  min-width: 0;
}
.csm-danger-title {
  margin: 0;
  font-size: var(--text-body-sm);
  font-weight: var(--weight-semibold);
  color: var(--danger-ink);
}
.csm-danger-sub {
  margin: 2px 0 0;
  font-size: var(--text-body-sm);
  color: var(--ink-600);
}
.csm-trash {
  font-size: 12px;
}
.csm-confirm-lede {
  margin: 0;
  font-size: var(--text-body);
  color: var(--ink-800);
}
.csm-confirm-list {
  margin: 0;
  padding-left: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  font-size: var(--text-body-sm);
  color: var(--ink-600);
}
.csm-error {
  background: var(--danger-soft);
  border-radius: var(--radius-md);
  padding: var(--space-md);
  color: var(--danger-ink);
  font-size: var(--text-body-sm);
}
.csm-actions {
  display: flex;
  gap: var(--space-md);
}
.csm-flex {
  flex: 1;
}
</style>
